<template>
  <div ref="root">
    <div :class="headerClass">
      <h3 v-if="showTitle" class="text-lg font-semibold text-gray-900">
        <slot name="title" />
      </h3>

      <!-- Filters and Search -->
      <div class="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
        <div class="relative w-full sm:w-[380px]">
          <div
            class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
          >
            <svg
              class="h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              ></path>
            </svg>
          </div>
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            :placeholder="searchPlaceholder"
            class="block w-full pl-10 pr-3 py-2 border border-gray-300 focus:outline-none rounded-lg text-sm focus:ring-brand-500 focus:border-brand-500"
            @input="handleSearch"
          />
        </div>
        <div class="flex gap-3">
          <!-- Year Filter -->
          <select
            v-model="selectedYear"
            @change="handleFilterChange"
            class="flex-1 sm:w-[140px] pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 bg-white appearance-none bg-no-repeat bg-right"
            :class="selectedYear === 'all' ? 'text-gray-500' : 'text-gray-900'"
            style="
              background-image: url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 fill=%27none%27 viewBox=%270 0 20 20%27%3E%3Cpath stroke=%27%236B7280%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27 stroke-width=%271.5%27 d=%27M6 8l4 4 4-4%27/%3E%3C/svg%3E');
              background-position: right 0.5rem center;
              background-size: 1.25rem 1.25rem;
            "
          >
            <option value="all">
              <Translation
                name="wage_calculations.all_years"
                default="All Years"
              />
            </option>
            <option v-for="year in availableYears" :key="year" :value="year">
              {{ year }}
            </option>
          </select>

          <!-- Status Filter -->
          <select
            v-model="selectedStatus"
            @change="handleFilterChange"
            class="flex-1 sm:w-[140px] pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-brand-500 focus:border-brand-500 bg-white appearance-none bg-no-repeat bg-right"
            :class="
              selectedStatus === 'all' ? 'text-gray-500' : 'text-gray-900'
            "
            style="
              background-image: url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 fill=%27none%27 viewBox=%270 0 20 20%27%3E%3Cpath stroke=%27%236B7280%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27 stroke-width=%271.5%27 d=%27M6 8l4 4 4-4%27/%3E%3C/svg%3E');
              background-position: right 0.5rem center;
              background-size: 1.25rem 1.25rem;
            "
          >
            <option value="all">
              <Translation
                name="wage_calculations.all_statuses"
                default="All Statuses"
              />
            </option>
            <option value="draft">
              <Translation
                name="wage_calculations.status_draft"
                default="Draft"
              />
            </option>
            <option value="submitted">
              <Translation
                name="wage_calculations.status_submitted"
                default="Submitted"
              />
            </option>
            <option value="in_audit">
              <Translation
                name="wage_calculations.status_in_audit"
                default="In Audit"
              />
            </option>
            <option value="archived">
              <Translation
                name="wage_calculations.status_archived"
                default="Archived"
              />
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- Table Component -->
    <LivingWageCalculationsTable
      :legacy="legacy"
      :calculations="paginatedCalculations"
      :locale="locale"
      :archive-url-template="archiveUrlTemplate"
      :copy-url-template="copyUrlTemplate"
      :report-url-template="reportUrlTemplate"
      :wizard-continue-url-template="wizardContinueUrlTemplate"
      :wizard-first-step-url-template="wizardFirstStepUrlTemplate"
      :reopen-url-template="reopenUrlTemplate"
    >
      <template #empty-state>
        <div class="text-center py-8 sm:py-12 px-4">
          <div
            class="mx-auto w-20 h-20 sm:w-24 sm:h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4"
          >
            <svg
              class="w-10 h-10 sm:w-12 sm:h-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              ></path>
            </svg>
          </div>
          <h3 class="text-base sm:text-lg font-medium text-gray-900 mb-2">
            <Translation
              name="wage_calculations.no_calculations"
              default="No calculations found"
            />
          </h3>
          <p
            v-if="!isFiltered"
            class="text-sm sm:text-base text-gray-500 mb-6 max-w-md mx-auto"
          >
            <Translation
              name="wage_calculations.no_calculations_message"
              default="Start by creating your first living wage gap calculation."
            />
          </p>
        </div>
      </template>
    </LivingWageCalculationsTable>

    <!-- Pagination -->
    <div v-if="filteredCalculations.length > 0">
      <div
        v-if="totalPages > 1"
        class="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-200 pt-4"
      >
        <div class="text-sm text-gray-700 order-2 sm:order-1">
          Page
          <span class="font-medium">{{ currentPage }}</span>
          of
          <span> {{ totalPages }}</span>
        </div>

        <div class="flex items-center space-x-2 order-1 sm:order-2">
          <button
            @click="prevPage"
            :disabled="currentPage === 1"
            :class="[
              'px-3 py-2 text-sm font-medium rounded-md transition-colors',
              currentPage === 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50',
            ]"
          >
            <Translation name="wage_calculations.previous" default="Previous" />
          </button>

          <div class="hidden sm:flex items-center space-x-1">
            <button
              v-for="(page, index) in paginationRange"
              :key="index"
              @click="page !== '...' && goToPage(page)"
              :disabled="page === '...'"
              :class="[
                'px-3 py-2 text-sm font-medium rounded-md transition-colors',
                page === currentPage
                  ? 'bg-brand-600 text-white'
                  : page === '...'
                  ? 'bg-white text-gray-400 cursor-default'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50',
              ]"
            >
              {{ page }}
            </button>
          </div>

          <button
            @click="nextPage"
            :disabled="currentPage === totalPages"
            :class="[
              'px-3 py-2 text-sm font-medium rounded-md transition-colors',
              currentPage === totalPages
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50',
            ]"
          >
            <Translation name="wage_calculations.next" default="Next" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, useTemplateRef } from "vue";
import LivingWageCalculationsTable from "./LivingWageCalculationsTable.vue";
import Translation from "./aft/Translation.vue";
import { useTranslation } from "./aft/useTranslations";

export default {
  components: { LivingWageCalculationsTable, Translation },
  name: "WageCalculationsList",
  props: {
    legacy: {
      type: Boolean,
      default: false,
    },
    showTitle: {
      type: Boolean,
      default: true,
    },
    calculations: {
      type: Array,
      required: true,
    },
    availableYears: {
      type: Array,
      default: () => [],
    },
    searchParam: {
      type: String,
      default: "",
    },
    yearParam: {
      type: String,
      default: "all",
    },
    statusParam: {
      type: String,
      default: "all",
    },
    enableUrlSync: {
      type: Boolean,
      default: true,
    },
    locale: {
      type: String,
      required: true,
    },
    exportUrl: {
      type: String,
      required: false,
    },
    reportUrlTemplate: {
      type: String,
      required: false,
    },
    copyUrlTemplate: {
      type: String,
      required: false,
    },
    wizardContinueUrlTemplate: {
      type: String,
      required: false,
    },
    wizardFirstStepUrlTemplate: {
      type: String,
      required: false,
    },
    archiveUrlTemplate: {
      type: String,
      required: false,
    },
    reopenUrlTemplate: {
      type: String,
      required: false,
    },
    listPath: {
      type: String,
      default: "wage-calculations",
    },
  },
  setup(props) {
    const searchInput = ref(null);
    const searchQuery = ref(props.searchParam);
    const selectedYear = ref(props.yearParam);
    const selectedStatus = ref(props.statusParam);
    const currentPage = ref(1);
    const root = useTemplateRef("root");
    const perPage = 6;
    const { _t: translate } = useTranslation();

    const headerClass = computed(() => {
      const base =
        "mb-4 md:mb-6 flex flex-col gap-4 md:flex-col xl:flex-row md:items-start xl:items-center";
      return props.showTitle
        ? `${base} md:justify-between`
        : `${base} md:justify-end`;
    });

    const searchPlaceholder = translate(
      "wage_calculations.search_placeholder",
      "Search calculations (facility ID, year, location…)"
    );

    const isFiltered = computed(
      () => filteredCalculations.value.length !== props.calculations.length
    );

    const filteredCalculations = computed(() => {
      let results = props.calculations;

      // Apply search filter
      if (searchQuery.value && searchQuery.value.trim() !== "") {
        const search = searchQuery.value.toLowerCase().trim();
        results = results.filter((calc) => {
          const facilityName = calc.facility.name?.toLowerCase() || "";
          const facilityLocation =
            calc.facility.facility_location?.toLowerCase() || "";
          const facilityId = (calc.facility.facility_id || "").toLowerCase();
          const countryName = calc.facility.country_name?.toLowerCase() || "";
          const city = calc.facility.city?.toLowerCase() || "";
          const reportingYear = String(calc.reporting_year || "");

          return (
            facilityName.includes(search) ||
            facilityLocation.includes(search) ||
            facilityId.includes(search) ||
            countryName.includes(search) ||
            city.includes(search) ||
            reportingYear.includes(search)
          );
        });
      }

      // Apply year filter
      if (selectedYear.value !== "all") {
        const year = parseInt(selectedYear.value);
        results = results.filter((calc) => calc.reporting_year === year);
      }

      if (selectedStatus.value !== "all") {
        results = results.filter(
          (calc) =>
            calc.frontend_status[0] === selectedStatus.value ||
            calc.frontend_status[1] === selectedStatus.value
        );
      }

      return results;
    });

    const totalPages = computed(() => {
      return Math.ceil(filteredCalculations.value.length / perPage);
    });

    const paginatedCalculations = computed(() => {
      const start = (currentPage.value - 1) * perPage;
      const end = start + perPage;
      return filteredCalculations.value.slice(start, end);
    });

    const paginationRange = computed(() => {
      const range = [];
      const total = totalPages.value;
      const current = currentPage.value;

      if (total <= 7) {
        // Show all pages if 7 or fewer
        for (let i = 1; i <= total; i++) {
          range.push(i);
        }
      } else {
        range.push(1);

        if (current > 3) {
          range.push("...");
        }

        const start = Math.max(2, current - 1);
        const end = Math.min(total - 1, current + 1);

        for (let i = start; i <= end; i++) {
          range.push(i);
        }

        if (current < total - 2) {
          range.push("...");
        }

        range.push(total);
      }

      return range;
    });

    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;

        root.value.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    };

    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        goToPage(currentPage.value + 1);
      }
    };

    const prevPage = () => {
      if (currentPage.value > 1) {
        goToPage(currentPage.value - 1);
      }
    };

    // Reset to page 1 when filters change
    const resetPagination = () => {
      currentPage.value = 1;
    };

    const buildListUrl = () => {
      const params = new URLSearchParams();
      if (searchQuery.value && searchQuery.value.trim() !== "") {
        params.set("search", searchQuery.value);
      }
      if (selectedYear.value !== "all") {
        params.set("year", selectedYear.value);
      }
      if (selectedStatus.value !== "all") {
        params.set("status", selectedStatus.value);
      }
      const queryString = params.toString();
      return queryString
        ? `/${props.locale}/${props.listPath}?${queryString}`
        : `/${props.locale}/${props.listPath}`;
    };

    const updateUrl = () => {
      if (props.legacy || !props.enableUrlSync) {
        return;
      }
      const newUrl = buildListUrl();
      window.history.pushState({}, "", newUrl);
    };

    let searchTimeout = null;
    const handleSearch = () => {
      // Reset to page 1 when search changes
      resetPagination();

      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }

      searchTimeout = setTimeout(() => {
        updateUrl();
      }, 500);
    };

    const handleFilterChange = () => {
      // Reset to page 1 when filters change
      resetPagination();
      // Status filter is applied server-side (e.g. "Archived" vs active list); reload so the server returns the correct set
      if (!props.legacy && props.enableUrlSync !== false) {
        window.location.href = buildListUrl();
        return;
      }
      updateUrl();
    };

    const handleExport = () => {
      // Build export URL with current filter parameters
      const params = new URLSearchParams();

      if (searchQuery.value && searchQuery.value.trim() !== "") {
        params.set("search", searchQuery.value);
      }

      if (selectedYear.value !== "all") {
        params.set("year", selectedYear.value);
      }

      if (selectedStatus.value !== "all") {
        params.set("status", selectedStatus.value);
      }

      const queryString = params.toString();
      // Check if exportUrl already has query parameters
      const separator = props.exportUrl.includes("?") ? "&" : "?";
      const exportUrl = queryString
        ? `${props.exportUrl}${separator}${queryString}`
        : props.exportUrl;

      // Trigger download by navigating to the export URL
      window.location.href = exportUrl;
    };

    // Connect export button click handler
    onMounted(() => {
      const exportButton = document.getElementById("export-calculations-btn");
      if (exportButton) {
        exportButton.addEventListener("click", handleExport);
      }
    });

    onUnmounted(() => {
      const exportButton = document.getElementById("export-calculations-btn");
      if (exportButton) {
        exportButton.removeEventListener("click", handleExport);
      }
    });

    return {
      headerClass,
      searchInput,
      searchQuery,
      searchPlaceholder,
      selectedYear,
      selectedStatus,
      filteredCalculations,
      isFiltered,
      paginatedCalculations,
      currentPage,
      totalPages,
      paginationRange,
      goToPage,
      nextPage,
      prevPage,
      handleSearch,
      handleFilterChange,
      handleExport,
      translate,
    };
  },
};
</script>
